/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useEffect, useState } from 'react';
import {
  Link,
  useParams,
  useNavigate,
  useLocation,
  useOutletContext
} from 'react-router-dom';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../store/userSlice';
// Actions
import {
  approveComment,
  unapproveComment,
  publishComment,
  unpublishComment
} from '../../main/comments/actions';
import { updateInstance } from '../../actions';
// BASE Components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
import BASELoading from '../../../@BASE/core/BASELoading';
import NavLinkAdapter from '../../../@BASE/core/NavLinkAdapter/NavLinkAdapter';
// Material UI Components
import { lighten } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Timeline from '@mui/lab/Timeline';
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator
} from '@mui/lab';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
// Custom Components
import moment from 'moment';
import authRoles from '../../auth/authRoles';
import IMICommentCardHeader from './IMICommentCardHeader';
import IMICommentContentCard from './IMICommentContentCard';
import CommentStatusBar from './CommentStatusBar';
import ReplyCard from './ReplyCard';
import RepliesHeader from './RepliesHeader';
import IMIReplyItem from './IMIReplyItem';

function IMICommentCard(props) {
  const dispatch = useDispatch();
  const { comment, user, searchText, resource } = props;
  const [approved, setApproved] = useState(() => comment.approved ?? false);
  // the following is to support when the approve button is clicked to update header status
  const [headerComment, setHeaderComment] = useState(() => comment);

  const handleApproval = () => {
    let data = {
      user: user._id || user.id,
      id: comment.id,
      approved: !approved,
      status: !approved ? 'approved' : 'pending'
    };

    dispatch(updateInstance('imi_comments')(data)).then(({ payload }) => {
      setApproved((prev) => !prev);
      setHeaderComment({
        ...comment,
        status: data.status
      });
    });
  };

  useEffect(() => {
    setApproved(comment.approved);
    setHeaderComment({
      ...comment
    });
  }, [comment]);

  return (
    <Card key={comment.id} className="mb-32 p-0 w-full border-1">
      <IMICommentCardHeader
        comment={headerComment}
        destination={comment.page.id}
      />
      <CardContent className="p-0">
        <IMICommentContentCard
          comment={comment}
          user={user}
          searchText={searchText}
          resource={resource}
        />
        {comment.replies.filter(
          (reply) =>
            authRoles.editor.includes(user.role) ||
            (reply?.review?.cycle !== 'prod_alpha_internal' &&
              reply?.review?.cycle !== 'prod_beta_internal' &&
              reply?.review?.cycle !== 'prod_final_internal')
        ).length > 0 && (
          <Box
            className="card-footer flex flex-col p-0 border-t-1"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? lighten(theme.palette.background.default, 0.4)
                  : lighten(theme.palette.background.default, 0.02)
            }}
          >
            <Timeline
              className="p-16 w-full"
              position="right"
              sx={{
                '& .MuiTimelineItem-root:before': {
                  display: 'none'
                }
              }}
            >
              <RepliesHeader />
              {comment.replies
                .filter(
                  (reply) =>
                    authRoles.editor.includes(user.role) ||
                    (reply?.review?.cycle !== 'prod_alpha_internal' &&
                      reply?.review?.cycle !== 'prod_beta_internal' &&
                      reply?.review?.cycle !== 'prod_final_internal')
                )
                .map((reply, index) => (
                  <IMIReplyItem
                    key={reply.id}
                    reply={reply}
                    last={comment.replies.length === index + 1}
                    user={user}
                    searchText={searchText}
                    resource={resource}
                  />
                ))}
            </Timeline>
          </Box>
        )}
      </CardContent>
      <CardActions className="flex justify-end">
        {authRoles.leadReviewer.includes(user.role) &&
          (!approved ? (
            <Button
              variant="contained"
              color="success"
              size="small"
              aria-label="Approve"
              startIcon={
                <BASESvgIcon size={16}>heroicons-outline:check</BASESvgIcon>
              }
              className="justify-end mr-8"
              onClick={() => handleApproval()}
            >
              Approve
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              aria-label="Unapprove"
              startIcon={
                <BASESvgIcon size={16}>heroicons-outline:x</BASESvgIcon>
              }
              className="justify-end mr-8"
              onClick={() => handleApproval()}
            >
              Unapprove
            </Button>
          ))}
      </CardActions>
    </Card>
  );
}

export default IMICommentCard;
